/* JS Speed Shop: selected-variant fitment data and details. */
(function () {
  'use strict';

  if (!customElements.get('jss-product-fitment')) {
    customElements.define('jss-product-fitment', class extends HTMLElement {
      connectedCallback() {
        this.onReady = () => this.initialize();
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', this.onReady, { once: true });
        } else {
          this.initialize();
        }
      }

      initialize() {
        if (this.initialized || !this.isConnected) return;
        this.initialized = true;
        this.status = this.querySelector('[data-fitment-status]');
        this.results = this.querySelector('[data-fitment-results]');
        this.cache = new Map();
        this.requestNumber = 0;
        try {
          const initial = JSON.parse(this.querySelector('[data-fitment-json]').textContent);
          this.validate(initial);
          this.cache.set(String(initial.id), initial);
          this.initialId = String(initial.id);
        } catch (error) {
          this.status.textContent = 'Vehicle fitment could not be loaded. Contact us to confirm your vehicle.';
          this.announce({ error: true });
          return;
        }
        this.onChange = (event) => {
          const main = this.mainProduct();
          if (!main || !main.contains(event.target)) return;
          const picker = event.target.closest('variant-radios, variant-selects');
          if (picker) {
            this.selectVariant(this.selectedId());
          } else if (event.target.matches('input[name="id"], select[name="id"]')) {
            this.selectVariant(this.selectedId());
          }
        };
        document.addEventListener('change', this.onChange);
        this.onPageShow = () => this.selectVariant(this.selectedId());
        window.addEventListener('pageshow', this.onPageShow);
        this.selectVariant(this.selectedId());
      }

      disconnectedCallback() {
        document.removeEventListener('DOMContentLoaded', this.onReady);
        document.removeEventListener('change', this.onChange);
        window.removeEventListener('pageshow', this.onPageShow);
        this.controller?.abort();
        this.requestNumber = (this.requestNumber || 0) + 1;
        this.initialized = false;
        this.activeId = null;
        this.announce({ error: true });
      }

      mainProduct() {
        return document.querySelector('[id^="MainProduct-"][data-product-id="' + this.dataset.productId + '"]');
      }

      selectedId() {
        const picker = this.mainProduct()?.querySelector('variant-selects, variant-radios');
        if (picker) {
          try {
            const variants = JSON.parse(picker.querySelector('script[type="application/json"]').textContent);
            const selects = [...picker.querySelectorAll('select')];
            const values = selects.length ? selects.map(el => el.value) : [...picker.querySelectorAll('fieldset')].map(el => el.querySelector('input:checked')?.value || '');
            if (values.length && Array.isArray(variants)) {
              const selected = variants.find(v => v.options?.length === values.length && values.every((value, i) => value === v.options[i]));
              return selected ? String(selected.id) : '';
            }
          } catch (_) { /* Use the theme's current variant when no option JSON is present. */ }
          if ('currentVariant' in picker) return picker.currentVariant ? String(picker.currentVariant.id) : '';
        }
        const input = this.mainProduct()?.querySelector('form[data-type="add-to-cart-form"] [name="id"], .product-variant-id');
        return input?.value || this.initialId;
      }

      validate(data) {
        if (!data || !/^\d+$/.test(String(data.id)) || !Array.isArray(data.applications) || data.applicationCount !== data.applications.length) throw new Error('Incomplete variant fitment data');
      }

      announce(state) {
        this.currentState = { productId: this.dataset.productId, variantId: this.activeId || '', ...state };
        document.dispatchEvent(new CustomEvent('jss:fitment-update', { detail: this.currentState }));
      }

      async selectVariant(id) {
        id = String(id || '');
        if (this.activeId === id) return;
        this.activeId = id;
        const request = ++this.requestNumber;
        this.controller?.abort();
        this.results.replaceChildren();
        this.announce({ loading: !!id, invalid: !id });
        if (!id) {
          this.status.textContent = 'Select an available product option to view its vehicle fitment.';
          return;
        }
        if (this.cache.has(id)) {
          this.render(this.cache.get(id));
          return;
        }
        this.status.textContent = 'Loading vehicle fitment for the selected option…';
        const controller = this.controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 15000);
        try {
          const url = new URL(this.dataset.productUrl, window.location.origin);
          url.searchParams.set('variant', id);
          url.searchParams.set('section_id', this.dataset.sectionId);
          const preview = new URL(location.href).searchParams.get('preview_theme_id');
          if (preview) url.searchParams.set('preview_theme_id', preview);
          const response = await fetch(url.toString(), { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
          if (!response.ok) throw new Error('Fitment request failed');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const payload = html.querySelector('jss-product-fitment [data-fitment-json]');
          const data = JSON.parse(payload.textContent);
          this.validate(data);
          if (String(data.id) !== id) throw new Error('Variant mismatch');
          if (!this.isConnected || request !== this.requestNumber) return;
          this.cache.set(id, data);
          this.render(data);
        } catch (error) {
          if (request !== this.requestNumber || !this.isConnected) return;
          this.activeId = null;
          this.status.textContent = 'Vehicle fitment could not be loaded for this option. Contact us to confirm your vehicle.';
          this.announce({ error: true });
        } finally { clearTimeout(timeout); }
      }

      element(tag, className, text) {
        const node = document.createElement(tag);
        if (className) node.className = className;
        if (text !== undefined) node.textContent = text;
        return node;
      }

      render(data) {
        this.announce({ data });
        this.results.replaceChildren();
        const applications = (data.applications || []).filter(app =>
          app.verified === true && app.make && app.model && Array.isArray(app.years) && app.years.length
        );
        const option = data.title && data.title !== 'Default Title' ? 'Selected option: ' + data.title + '. ' : '';
        if (data.fitmentClass === 'universal') {
          this.status.textContent = option + 'Universal parts. Check product dimensions and installation requirements before ordering.';
          return;
        }
        if (!applications.length) {
          this.status.textContent = option + 'Fitment has not yet been confirmed for this option. Contact us with your vehicle details before ordering.';
          return;
        }
        this.status.textContent = option + 'Check the listed years, engine and all restrictions before ordering.';
        const makes = new Map();
        applications.forEach(app => {
          if (!makes.has(app.make)) makes.set(app.make, new Map());
          const models = makes.get(app.make);
          if (!models.has(app.model)) models.set(app.model, []);
          models.get(app.model).push(app);
        });
        for (const [make, models] of makes) {
          const makeDetails = this.element('details', 'jss-fitment__make');
          makeDetails.append(this.element('summary', '', make));
          const modelList = this.element('div', 'jss-fitment__models');
          for (const [model, records] of models) {
            const modelDetails = this.element('details', 'jss-fitment__model');
            modelDetails.append(this.element('summary', '', model));
            records.forEach(app => {
              const record = this.element('div', 'jss-fitment__record');
              const fields = this.element('dl');
              const addField = (label, value) => {
                if (!value) return;
                fields.append(this.element('dt', '', label), this.element('dd', '', value));
              };
              addField('Years', app.years.join(', '));
              addField('Engine', [app.engine, app.type].filter(Boolean).join(' · '));
              addField('Body', app.body);
              record.append(fields);
              if (app.notes) {
                const notes = this.element('p', 'jss-fitment__notes');
                notes.append(this.element('strong', '', 'Fitment notes: '), document.createTextNode(app.notes));
                record.append(notes);
              }
              modelDetails.append(record);
            });
            modelList.append(modelDetails);
          }
          makeDetails.append(modelList);
          this.results.append(makeDetails);
        }
      }
    });
  }

})();
